import { useEffect, useMemo, useState } from 'react'
import { InternalIdInput } from '@library'
import { EventFlowsForElementModal } from '@mobileComponents/EventFlow'
import { DrawerItem, MobileEventFlow, VectorIcon } from '@mobileTypes'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'
import { IconPickerField } from '@microservices/wiskey-react-components'
import { ICON_REGISTRY } from '@mobileConstants'
import { ColorPickerField } from '@components/ColorPicker'
import { ScriptValueDialog, Variable as ScriptVariable } from '@microservices/wiskey-react-components'
import moment from 'moment'

interface DrawerItemDialogProps {
  open: boolean
  item: DrawerItem
  onClose: () => void
  onSave: (item: DrawerItem) => void
  /** When provided, shows "View event flows" and opens modal with flows for this drawer item */
  eventFlows?: MobileEventFlow[]
  drawerItemIndex?: number
  /** Full list of drawer items (for flow creation and drawer item selector) */
  drawerItems?: DrawerItem[]
  /** Called when event flows change from the element modal (add/edit/delete flow or action) */
  onEventFlowsChange?: (flows: MobileEventFlow[]) => void
}

export const DrawerItemDialog = ({
  open,
  item,
  onClose,
  onSave,
  eventFlows = [],
  drawerItemIndex,
  drawerItems = [],
  onEventFlowsChange,
}: DrawerItemDialogProps) => {
  const [internalId, setInternalId] = useState(item.internalId)
  const [label, setLabel] = useState(item.label)
  const [isJs, setIsJs] = useState<boolean>(Boolean((item as any).isJs))
  const [icon, setIcon] = useState<VectorIcon | null>(item.icon ?? null)
  const [errors, setErrors] = useState<{ internalId?: string; label?: string }>({})
  const [eventFlowsModalOpen, setEventFlowsModalOpen] = useState(false)
  const [showLabelJsEditor, setShowLabelJsEditor] = useState(false)
  const showEventFlows =
    typeof drawerItemIndex === 'number' && drawerItemIndex >= 0 && Boolean(onEventFlowsChange)

  const scriptVariables: ScriptVariable[] = useMemo(
    () => [
      {
        name: 'globalVariables',
        type: 'any',
        description: 'Global variables object from mobile app',
      },
      {
        name: 'moment',
        type: 'typeof moment',
        description: 'Moment.js library helper',
      },
    ],
    []
  )

  useEffect(() => {
    if (open) {
      setInternalId(item.internalId)
      setLabel(item.label)
      setIsJs(Boolean((item as any).isJs))
      setIcon(item.icon ?? null)
      setErrors({})
    }
  }, [open, item])

  const handleSave = () => {
    const newErrors: { internalId?: string; label?: string } = {}

    if (!internalId.trim()) {
      newErrors.internalId = 'Internal ID is required'
    }
    if (!label.trim()) {
      newErrors.label = 'Label is required'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)

      return
    }

    onSave({ internalId: internalId.trim(), label, icon, isJs })
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
        <DialogTitle>Drawer Item</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {showEventFlows && (
              <Box sx={{ pb: 1 }}>
                <Button
                  size='small'
                  startIcon={<EventAvailableOutlinedIcon />}
                  onClick={() => setEventFlowsModalOpen(true)}
                >
                  View event flows for this item
                </Button>
              </Box>
            )}
            <InternalIdInput
              fullWidth
              label='Internal ID'
              value={internalId}
              onChange={setInternalId}
              error={!!errors.internalId}
              helperText={errors.internalId}
              required
              placeholder='e.g. settings_screen'
            />
            <IconPickerField
              value={icon}
              onChange={newIcon => setIcon(newIcon)}
              iconRegistry={ICON_REGISTRY}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                fullWidth
                label={isJs ? 'Label (JS expression)' : 'Label'}
                value={label}
                onChange={e => setLabel(e.target.value)}
                error={!!errors.label}
                helperText={
                  errors.label ||
                  (isJs
                    ? 'JavaScript code that returns label string (uses globalVariables, moment)'
                    : 'Display label for the drawer item')
                }
                required
                disabled={isJs}
              />
              <Button
                variant={isJs ? 'contained' : 'outlined'}
                onClick={() => {
                  setIsJs(true)
                  setShowLabelJsEditor(true)
                }}
              >
                {isJs ? 'Edit JS' : 'Use JS'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} variant='contained'>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {showEventFlows && onEventFlowsChange && (
        <EventFlowsForElementModal
          open={eventFlowsModalOpen}
          onClose={() => setEventFlowsModalOpen(false)}
          title='Event flows for this drawer item'
          eventFlows={eventFlows}
          elementRef={{ type: 'drawer_item', drawerItemInternalId: item.internalId }}
          drawerItems={drawerItems}
          onEventFlowsChange={onEventFlowsChange}
        />
      )}
      {showLabelJsEditor && (
        <ScriptValueDialog
          isShow
          value={label || ''}
          title='Edit Drawer Item Label JS'
          hint='Return a string label. Available: globalVariables, moment.'
          onClose={() => setShowLabelJsEditor(false)}
          onSave={code => {
            setLabel(code)
            setIsJs(true)
            setShowLabelJsEditor(false)
          }}
          language='js'
          variables={scriptVariables}
          fieldsInfo={[]}
        />
      )}
    </>
  )
}
